/**
 * Shared WhatsApp approved-template composer (Compose + Bulk Send).
 * - Select an APPROVED template (Meta won't deliver business-initiated messages
 *   without one).
 * - Upload the header media to public S3 (useFileUpload) when the template has an
 *   image/video/document header.
 * - Map each body variable ({{n}}) and dynamic URL button parameter.
 * - Live preview (right) of exactly how the message will look.
 *
 * Emits a ComposerOutput (incl. validation errors) to the parent via onChange.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { IconPhoto, IconVideo, IconFile, IconCheck } from '@tabler/icons-react';
import { FileButton } from '@mantine/core';
import { Stack, Group, Text, TextInput, Select, Button, Badge, Alert } from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';
import { useDialog360 } from '../WhatsApp/hooks/useDialog360';
import { useFileUpload } from '../../../../components/ccms-components/file-management/hooks';
import {
  parseTemplate, validateTemplateInputs, mediaAccept, type WaTemplateStructure,
} from './whatsappTemplate';
import type { TemplateListItem } from '../WhatsApp/dialog360.types';

export interface ComposerOutput {
  template: TemplateListItem | null;
  templateName: string;
  templateLanguage: string;
  structure: WaTemplateStructure | null;
  mediaUrl: string;
  bodyValues: string[];
  buttonUrlParam: string;
  errors: string[];
}

const uid = (t: TemplateListItem) => String(t.id || t.externalId || `${t.name}|${t.language || ''}`);

export function WhatsAppTemplateComposer({
  mode,
  sampleBodyValues,
  onChange,
}: {
  mode: 'single' | 'bulk';
  sampleBodyValues?: string[];
  onChange: (o: ComposerOutput) => void;
}) {
  const { loading, cloudTemplates, fetchCloudTemplates } = useDialog360();

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaName, setMediaName] = useState('');
  const [bodyValues, setBodyValues] = useState<string[]>([]);
  const [buttonUrlParam, setButtonUrlParam] = useState('');

  useEffect(() => {
    fetchCloudTemplates();
  }, [fetchCloudTemplates]);

  // Approved templates only, deduped by unique id.
  const approved = useMemo(() => {
    const list = (cloudTemplates as TemplateListItem[]).filter((t) => String(t.status || '').toLowerCase() === 'approved');
    return Array.from(new Map(list.map((t) => [uid(t), t])).values());
  }, [cloudTemplates]);

  const template = useMemo(() => approved.find((t) => uid(t) === templateId) ?? null, [approved, templateId]);
  const structure = useMemo(() => (template ? parseTemplate(template) : null), [template]);

  const { upload, uploading } = useFileUpload({
    domain: 'Communications',
    recordId: 'whatsapp-send',
    maxSize: 16 * 1024 * 1024,
    onComplete: (files) => { if (files[0]?.url) setMediaUrl(files[0].url); },
  });

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const bodyKey = bodyValues.join('|');
  useEffect(() => {
    const valuesForValidation = mode === 'single' ? bodyValues : (sampleBodyValues ?? []);
    const errors = structure
      ? validateTemplateInputs(structure, {
          mediaUrl,
          // In bulk mode body values come per-recipient from the CSV, so we don't block here.
          bodyValues: mode === 'single' ? bodyValues : Array.from({ length: structure.bodyVarCount }, () => 'x'),
          buttonUrlParam,
        })
      : ['Select an approved template.'];
    onChangeRef.current({
      template,
      templateName: template?.name ?? '',
      templateLanguage: template?.language ?? 'en',
      structure,
      mediaUrl,
      bodyValues: valuesForValidation,
      buttonUrlParam,
      errors,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, mediaUrl, bodyKey, buttonUrlParam, structure, mode, (sampleBodyValues ?? []).join('|')]);

  const handlePickMedia = async (file: File | null) => {
    if (!file) return;
    setMediaName(file.name);
    setMediaUrl('');
    await upload([file]);
  };

  const onSelectTemplate = (id: string | null) => {
    setTemplateId(id);
    setMediaUrl(''); setMediaName(''); setBodyValues([]); setButtonUrlParam('');
  };

  return (
    <Stack gap="sm">
        <Select
          label="Approved template"
          placeholder={loading ? 'Loading…' : 'Select an approved template'}
          description="WhatsApp requires an approved template for business-initiated messages."
          data={approved.map((t) => ({ value: uid(t), label: `${t.name}${t.language ? ` (${t.language})` : ''}` }))}
          value={templateId}
          onChange={onSelectTemplate}
          searchable
          required
          nothingFoundMessage="No approved templates"
        />

        {!loading && approved.length === 0 && (
          <Alert color="yellow">No approved templates found. Create one under Templates and wait for Meta approval.</Alert>
        )}

        {structure?.requiresMedia && (
          <Card withBorder padding="sm">
            <Group justify="space-between">
              <Text size="sm" fw={500}>{structure.headerFormat} header (uploaded to S3)</Text>
              <FileButton onChange={handlePickMedia} accept={mediaAccept(structure.headerFormat)}>
                {(props) => (
                  <Button {...props} size="xs" variant="light" loading={uploading}
                    leftSection={structure.headerFormat === 'IMAGE' ? <IconPhoto size={14} /> : structure.headerFormat === 'VIDEO' ? <IconVideo size={14} /> : <IconFile size={14} />}>
                    {mediaUrl ? 'Replace' : 'Upload'} {structure.headerFormat.toLowerCase()}
                  </Button>
                )}
              </FileButton>
            </Group>
            {mediaName && (
              <Group gap="xs" mt="xs">
                {mediaUrl ? <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>Uploaded</Badge> : <Badge color="gray" variant="light">Uploading…</Badge>}
                <Text size="xs" c="dimmed">{mediaName}</Text>
              </Group>
            )}
          </Card>
        )}

        {structure && structure.bodyVarCount > 0 && (
          mode === 'single' ? (
            <Stack gap={6}>
              <Text size="sm" fw={500}>Body variables</Text>
              {Array.from({ length: structure.bodyVarCount }, (_, i) => (
                <TextInput
                  key={i}
                  size="sm"
                  label={`Variable {{${i + 1}}}`}
                  value={bodyValues[i] ?? ''}
                  onChange={(e) => setBodyValues((prev) => { const next = [...prev]; next[i] = e.currentTarget.value; return next; })}
                  required
                />
              ))}
            </Stack>
          ) : (
            <Alert color="blue">
              This template needs {structure.bodyVarCount} variable(s). They are filled per-recipient from your file columns (after the recipient column), in order.
            </Alert>
          )
        )}

        {structure && structure.dynamicUrlButtonIndex >= 0 && (
          <TextInput
            label="Dynamic button URL value"
            description="Fills the {{1}} in the template's URL button"
            value={buttonUrlParam}
            onChange={(e) => setButtonUrlParam(e.currentTarget.value)}
            required
          />
        )}
    </Stack>
  );
}
